import React from 'react';
import {
  Container,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { useConfig } from '../context/ConfigContext';


const AboutTheSalaryCalculator = () => {
  const { config } = useConfig();

  return (
    <Container sx={{ padding: '0 20px' }}>
      <Helmet>
        <title>Tax Year 25/26 UK Take Home Pay Calculation Overview | SalaryTools.co.uk</title>
        <meta
          name="description"
          content="Explore the detailed calculation overview for the tax year 25/26 on SalaryTools.co.uk. Learn how Income Tax, National Insurance, Student Loan repayments, and Pension Contributions are computed to determine your final UK take-home pay."
        />
        <link rel="canonical" href="https://salarytools.co.uk/about-the-salary-calculator" />
      </Helmet>

      <Box sx={{ pt: 8, mb: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Tax Year 25/26 UK Take Home Pay Calculation Overview
        </Typography>

      {/* Income Tax Section */}
      <Box id="income-tax" sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Income Tax
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          In the UK, Income Tax is levied on the portion of your earnings that exceed your tax‐free personal allowance. For the tax year 2025, the standard personal allowance is <strong>£{config.PERSONAL_ALLOWANCE.toLocaleString()}</strong>. 
          If your total income exceeds the threshold of <strong>£{config.PERSONAL_ALLOWANCE_THRESHOLD.toLocaleString()}</strong>, your allowance is tapered – reducing by £1 for every £2 earned above this limit. This tapering is based on your adjusted net income, which factors in deductions like pension contributions.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          The tax bands below indicate how Income Tax is applied to your taxable income:
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
          <Table sx={{ maxWidth: 500 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Income Range (£)</strong></TableCell>
                <TableCell><strong>Tax Rate (%)</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.TAX_BANDS.map((band, index) => (
                <TableRow key={index}>
                  <TableCell>
                    £{band.min.toLocaleString()} – {band.max.toLocaleString()}
                  </TableCell>
                  <TableCell>{band.rate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2">
          Other allowances, such as Blindness or Married Couple’s Allowance, may apply under certain circumstances but are not factored into this calculation.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* National Insurance Section */}
      <Box id="national-insurance" sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          National Insurance
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          National Insurance (NI) contributions are deducted from your gross earnings to fund state benefits and pensions. For 2025, NI works as follows:
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          - <strong>Below the lower threshold:</strong> No NI is due.<br />
          - <strong>Between the lower and upper thresholds:</strong> Earnings attract a rate of <strong>{(config.NI.MIDDLE_RATE * 100).toFixed(0)}%</strong>.<br />
          - <strong>Above the upper threshold:</strong> A lower rate of <strong>{(config.NI.UPPER_RATE * 100).toFixed(0)}%</strong> applies.
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
          <Table sx={{ maxWidth: 700 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell><strong>Rate (%)</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Below Lower Threshold of £{config.NI.LOWER_THRESHOLD.toLocaleString()} annually</strong>
                </TableCell>
                <TableCell>0%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Between Lower & Upper Threshold of £{config.NI.LOWER_THRESHOLD.toLocaleString()} and £{config.NI.UPPER_THRESHOLD.toLocaleString()} annually</strong>
                </TableCell>
                <TableCell>{(config.NI.MIDDLE_RATE * 100).toFixed(0)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Above Upper Threshold of £{config.NI.UPPER_THRESHOLD.toLocaleString()} annually</strong>
                </TableCell>
                <TableCell>{(config.NI.UPPER_RATE * 100).toFixed(0)}%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2">
          Note: These thresholds are updated periodically to reflect economic changes.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Student Loan Section */}
      <Box id="student-loan" sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Student Loan Repayments
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Student Loan repayments are determined by your loan plan and are calculated based on your gross income (including overtime) above a specified threshold.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          For 2025, the details are:
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
          <Table sx={{ maxWidth: 700 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Threshold (£)</strong></TableCell>
                <TableCell><strong>Repayment Rate (%)</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(config.STUDENT_LOANS).map(([plan, details]) => (
                <TableRow key={plan}>
                  <TableCell>
                    <strong>{plan.charAt(0).toUpperCase() + plan.slice(1)}</strong>
                  </TableCell>
                  <TableCell>£{details.threshold.toLocaleString()}</TableCell>
                  <TableCell>{details.rate}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2">
          Postgraduate loans are managed separately at a rate of {config.STUDENT_LOANS.postgraduate.rate}% on earnings above £{config.STUDENT_LOANS.postgraduate.threshold.toLocaleString()}.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Pension Contributions Section */}
      <Box id="pension-contributions" sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Pension Contributions
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Pension contributions help lower your taxable income, which in turn affects your Income Tax and National Insurance. The method by which contributions are deducted varies by the type of pension scheme.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Personal Pension Schemes:</strong> For personal pensions, the amount you enter is treated as your net contribution. This figure is boosted by the basic rate of tax (multiplied by 1.25) to approximate the full, or gross, contribution. If you fall into a higher tax bracket, additional relief is applied, and this adjusted amount is subtracted from your gross income before tax is calculated.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Other Pension Schemes:</strong> For schemes such as Auto-enrolment, Employer/Occupational, and Salary Sacrifice, your contribution is directly deducted from your gross earnings. This reduces both your taxable income and the base on which National Insurance is calculated.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          There is a yearly cap on the amount that qualifies for these tax benefits – currently set at £60,000. Exceeding this limit may result in additional tax liabilities.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Auto-enrolment Schemes:</strong> These are mandatory for eligible workers, where contributions are applied only to the portion of your income between defined lower and upper limits (roughly £6,240 to £50,270). Your base salary along with any overtime or bonus earnings are used for this calculation.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Employer/Occupational Schemes:</strong> Contributions under these plans are taken from your earnings before tax, which directly reduces your taxable income.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Salary Sacrifice Schemes:</strong> Under these arrangements, you agree to a reduction in your salary in exchange for an equivalent pension contribution. This lowers your reported salary, thereby reducing your tax, National Insurance, and student loan obligations.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Overall Calculation Process Section */}
      <Box id="overall-calculations" sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Overall Calculation Process
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Our calculator brings together all these factors – including your base salary, any overtime, pension contributions, Income Tax, National Insurance, and student loan repayments – to compute your final take-home pay. The steps include:
        </Typography>
        <ul>
          <li>
            <Typography variant="body1">
              <strong>Pension Contributions:</strong> Depending on your scheme, contributions are either boosted (in personal schemes) or directly deducted, reducing your gross income.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Personal Allowance Adjustment:</strong> Your adjusted income (after pension deductions) is then used to calculate your personal allowance. If your income exceeds the allowance threshold, your tax-free amount is reduced accordingly.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Income Tax Calculation:</strong> Tax is computed on your taxable income based on the relevant tax bands.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>National Insurance and Student Loan Repayments:</strong> These are calculated on your gross earnings, with the necessary adjustments, to derive your final net income.
            </Typography>
          </li>
        </ul>
        <Typography variant="body1">
          This step-by-step process ensures every aspect of your income is taken into account, giving you an accurate picture of your net take-home pay.
        </Typography>
      </Box>
      </Box>
    </Container>
  );
};

export default AboutTheSalaryCalculator;
